import {
    IApplication, IApplicationModule,
    IApplicationSuite,
    IApplicationSuiteType,
    IExecutable, IModuleReport,
    IUIFramework,
    IUIProfile
} from "./PA.UIM.Core.IF.Framework";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {ASync} from "../PA.Core/Basic/ASync";
import * as _ from "lodash"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_UI_App_Report, esWB_UI_AppExecutable,
    esWB_UI_Application, esWB_UI_AppModule,
    esWB_UI_AppSuite,
    esWB_UI_AppSuite_App_Relationship, esWB_UI_AppSuite_Type,
    esWB_UI_Profile,
} from "../PA.Core/PAdbSchema";
import {ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {
    WB_UIAppExecutableRow,
    WB_UIApplicationRow, WB_UIAppReportRow,
    WB_UIAppSuiteRow,
    WB_UIAppSuiteTypeRow,
    WB_UIProfileRow
} from "../PA.Core/PAdbSchemaRows";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {IUsrContext} from "../PA.Core/PA.Core.IF.User";
import {JSONUtils} from "../PA.Core/Basic/JSON";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {ISearch} from "./PA.UIM.Core.IF.Search";


export class UIFramework
    implements IUIFramework {
    constructor() {
        this._progressProvider = new ProgressProvider()
        this._messageProvider = new MessageProvider()
        this._bIsFrameworkLoaded = false
        this._OnLoadComplete = undefined

        this._dctUIProfiles = {}
        this._uiProfiles = []
        this._dctApplicationSuiteTypes = {}
        this._applicationSuiteTypes = []
        this._dctApplicationSuites = {}
        this._applicationSuites = []
        this._dctApplications = {}
        this._applications = []
        this._dctExecutable = {}
        this._executables = []
        this._reports = []
        this._dctReport = {}
    }

    private _messageProvider: MessageProvider
    private _progressProvider: ProgressProvider
    private _bIsFrameworkLoaded: boolean;
    private _OnLoadComplete: Promise<void>

    private _uiProfiles: IUIProfile[]
    private _dctUIProfiles: { [wbId: number]: IUIProfile }

    private _applicationSuiteTypes: IApplicationSuiteType[]
    private _dctApplicationSuiteTypes: { [wbId: number]: IApplicationSuiteType }

    private _applicationSuites: IApplicationSuite[]
    private _dctApplicationSuites: { [wbId: number]: IApplicationSuite }

    private _applications: IApplication[]
    private _dctApplications: { [wbId: number]: IApplication }

    private _executables: IExecutable[]
    private _dctExecutable: { [wbId: number]: IExecutable }

    private _reports: IModuleReport[]
    private _dctReport: { [wbId: string]: IModuleReport }

    get MessageProvider(): MessageProvider {
        return this._messageProvider
    }

    get ProgressProvider(): ProgressProvider {
        return this._progressProvider
    }

    get bIsFrameworkLoaded(): boolean {
        return this._bIsFrameworkLoaded
    }


    get OnLoadComplete(): Promise<void> {
        const self = this

        if (this._bIsFrameworkLoaded) {
            return ASync.resolve()
        }

        if (!_.isUndefined(self._OnLoadComplete)) {
            return self._OnLoadComplete
        }

        let usrContext: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)


        const returnValue = self._OnLoadComplete = new Promise<void>((resolve, error) => {
            let dbContext = new PAdbContext()


            let recordResolver = new ESRecordResolver(dbContext, {
                standardConditions: {
                    [esWB_UI_Profile]: {"DELETED_IND": {"notIn": ['1']}},
                    [esWB_UI_AppSuite_Type]: {"DELETED_IND": {"notIn": ['1']}},
                    [esWB_UI_AppSuite]: {"DELETED_IND": {"notIn": ['1']}},
                    [esWB_UI_AppSuite_App_Relationship]: {"DELETED_IND": {"notIn": ['1']}},
                    [esWB_UI_Application]: {"DELETED_IND": {"notIn": ['1']}},
                    [esWB_UI_AppModule]: {"DELETED_IND": {"notIn": ['1']}},
                    [esWB_UI_App_Report]: {"DELETED_IND": {"notIn": ['1']}},
                    [esWB_UI_AppExecutable]: {"DELETED_IND": {"notIn": ['1']}}
                },
                dctIncludeBelongsToNamedRelations: {
                    [esWB_UI_Application]: [esWB_UI_AppModule, esWB_UI_App_Report]
                }
            })

            // Read in Startup Applications First..they may not be related to a Application Suite
            recordResolver.queryAllWithRelationships(esWB_UI_Application, {STARTUP_POLICY: 'AutoStart'})
                .then((results: WB_UIApplicationRow[]) => {

                    _.forEach(results, (anApp) => {
                        self.registerApplicationRow(anApp)
                    })

                    // Read in App Suites
                    return recordResolver.queryAllWithRelationships(esWB_UI_AppSuite, {where: {"DELETED_IND": {"notIn": ['1']}}})
                })

                .then((arrAppSuitesRows: WB_UIAppSuiteRow[]) => {

                    //--
                    _.forEach(arrAppSuitesRows, (anAppSuiteRow) => {
                        if (!anAppSuiteRow.isActive || _.isUndefined(anAppSuiteRow.WB_UIProfileRow) || !anAppSuiteRow.WB_UIProfileRow.isActive)
                            return

                        let aUIProfile: IUIProfile = self.registerUIProfileRow(anAppSuiteRow.WB_UIProfileRow)
                        if (_.isEmpty(aUIProfile)) {
                            return
                        }

                        let AppSuiteType: IApplicationSuiteType = self.registerAppSuiteTypeRow(anAppSuiteRow.WB_UIAppSuiteTypeRow)
                        if (_.isEmpty(AppSuiteType)) {
                            return
                        }

                        let anAppSuite: IApplicationSuite = self.registerAppSuiteRow(anAppSuiteRow)

                    })

                    // Should we register Applications/Executables not in profiles ?
                    //  this would allow for someone to jump to Master data from a Data Studio.., even if it's not in the UI Profile

                    self._bIsFrameworkLoaded = true
                    resolve()

                })
                .catch((err) => {
                    error(err)
                })

        })

        return returnValue
    }


    private registerAppSuiteTypeRow(anAppSuiteTypeRow: WB_UIAppSuiteTypeRow): IApplicationSuiteType {
        const self = this

        if (_.isEmpty(anAppSuiteTypeRow)) {
            return undefined
        }

        let AppSuiteType: IApplicationSuiteType = undefined
        if (anAppSuiteTypeRow.cif.WB_UI_APPSUITEM_ID in self._dctApplicationSuiteTypes) {
            AppSuiteType = self._dctApplicationSuiteTypes[anAppSuiteTypeRow.cif.WB_UI_APPSUITEM_ID]
        } else {
            //-- Add App Suite Type
            AppSuiteType = {
                wb_id: anAppSuiteTypeRow.cif.WB_UI_APPSUITEM_ID,
                id_ui: anAppSuiteTypeRow.cif.APPSUITETYPE_ID,
                name: anAppSuiteTypeRow.cif.APPSUITETYPE_NAME,
                sort: anAppSuiteTypeRow.cif.SORT_ORDER,
                renderOptions: JSONUtils.parse(anAppSuiteTypeRow.cif.RENDER_OPTIONS, {})
            }

            self._dctApplicationSuiteTypes[AppSuiteType.wb_id] = AppSuiteType
            self._applicationSuiteTypes.push(AppSuiteType)
        }

        return AppSuiteType
    }

    private registerUIProfileRow(aWB_UIProfileRow: WB_UIProfileRow): IUIProfile {
        const self = this

        let usrContext: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        let aUIProfile: IUIProfile = undefined

        if (_.isEmpty(aWB_UIProfileRow)) {
            return undefined
        }

        // Check if we need to add the profile to the
        let bHasAccess = true
        if (aWB_UIProfileRow.cif.WB_UI_PROFILE_ID in self._dctUIProfiles) {
            aUIProfile = self._dctUIProfiles[aWB_UIProfileRow.cif.WB_UI_PROFILE_ID]
        } else {
            // Check the roles
            //bHasAccess = false // Fix when roles added
            let ViewRoleCSV = aWB_UIProfileRow.cif.VIEW_ROLES
            let arrProfileRoles = ViewRoleCSV.split(",")
            //_.forEach(arrProfileRoles, (aRoleId) => {
            //    if (usrContext.hasRoleId(aRoleId)) {
            //       bHasAccess = true
            //   }
            // })

            aUIProfile = {
                wb_id: aWB_UIProfileRow.cif.WB_UI_PROFILE_ID,
                id_ui: aWB_UIProfileRow.cif.PROFILE_ID,
                name: aWB_UIProfileRow.cif.PROFILE_NAME,
                sort: aWB_UIProfileRow.cif.SORT_ORDER,
                roles: {},
                renderOptions: JSONUtils.parse(aWB_UIProfileRow.cif.RENDER_OPTIONS, {})
            }

            let bHasRole = false
            _.forEach(arrProfileRoles, (aRoleId) => {
                if (usrContext.hasRoleId(aRoleId)) {
                    bHasRole = true
                    aUIProfile.roles[aRoleId] = true
                } else
                    aUIProfile.roles[aRoleId] = false

            })

            if (bHasRole) {
                self._uiProfiles.push(aUIProfile)
            }
            self._dctUIProfiles[aUIProfile.wb_id] = aUIProfile
        }

        return aUIProfile
    }

    private registerAppSuiteRow(anAppSuiteRow: WB_UIAppSuiteRow): IApplicationSuite {
        const self = this

        if (_.isEmpty(anAppSuiteRow)) {
            return undefined
        }

        let anAppSuite: IApplicationSuite = {
            wb_id: anAppSuiteRow.cif.WB_UI_APPSUITE_ID,
            id_ui: anAppSuiteRow.cif.APPSUITE_ID,
            name: anAppSuiteRow.cif.APPSUITE_NAME,
            description: anAppSuiteRow.cif.APPSUITE_DESCRIPTION,
            sort: anAppSuiteRow.cif.SORT_ORDER,
            ui5Icon: anAppSuiteRow.cif.UI5_ICON,
            type: self._dctApplicationSuiteTypes[anAppSuiteRow.cif.WB_UI_APPSUITEM_ID],
            profile: self._dctUIProfiles[anAppSuiteRow.cif.WB_UI_PROFILE_ID],
            renderOptions: JSONUtils.parse(anAppSuiteRow.cif.RENDER_OPTIONS, {}),
            applicationRels: []
        }

        // Add Applications Rels
        _.forEach(anAppSuiteRow.WB_UIAppSuiteAppRelationshipRows, (anAppRelRow) => {
            if (!anAppRelRow.isActive) {
                return
            }

            if (_.isUndefined(anAppRelRow.WB_UIApplicationRow) || !anAppRelRow.WB_UIApplicationRow.isActive)
                return

            let anApplication = self.registerApplicationRow(anAppRelRow.WB_UIApplicationRow)
            if (_.isEmpty(anApplication)) {
                return
            }

            anAppSuite.applicationRels.push({
                application: anApplication,
                appName: anAppRelRow.cif.APP_NAME_OVRD || anApplication.name,
                groupName: anAppRelRow.cif.GROUP_NAME_OVRD || anApplication.groupName,
                bAutoLaunch: anAppRelRow.cif.AUTO_LAUNCH_IND == '1',
                launchPolicy: anAppRelRow.cif.LAUNCH_POLICY,
                sort: anAppRelRow.cif.SORT_ORDER,
                ui5Icon: anAppRelRow.cif.UI5_ICON_OVRD || anApplication.ui5Icon,
                renderOptions: JSONUtils.parse(anAppRelRow.cif.RENDER_OPTIONS, {}),
                appOverrideName: anAppRelRow.cif.APP_NAME_OVRD
            })

        })

        if (anAppSuite.applicationRels.length) {
            self._dctApplicationSuites[anAppSuite.wb_id] = anAppSuite
            self._applicationSuites.push(anAppSuite)
            return anAppSuite
        }
        return undefined
    }

    private registerApplicationRow(anApplicationRow: WB_UIApplicationRow): IApplication {
        const self = this

        if (_.isEmpty(anApplicationRow)) {
            return undefined
        }
        if (anApplicationRow.cif.WB_UI_APP_ID in self._dctApplications) {
            return self._dctApplications[anApplicationRow.cif.WB_UI_APP_ID]
        }

        let usrContext: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)

        let anApplication: IApplication = {
            wb_id: anApplicationRow.cif.WB_UI_APP_ID,
            id_ui: anApplicationRow.cif.APP_ID,
            name: anApplicationRow.cif.APP_NAME,
            groupName: anApplicationRow.cif.GROUP_NAME,
            startupPolicy: anApplicationRow.cif.STARTUP_POLICY,
            launchPolicy: anApplicationRow.cif.LAUNCH_POLICY,
            moduleBarPolicy: anApplicationRow.cif.MODULE_BAR_POLICY,
            taskBarPolicy: anApplicationRow.cif.TASK_BAR_POLICY,
            appfinderPolicy: anApplicationRow.cif.APPFINDER_POLICY,
            reportModeDefault: anApplicationRow.cif.REPORT_MODE_DFLT,
            sort: anApplicationRow.cif.SORT_ORDER,
            ui5Icon: anApplicationRow.cif.UI5_ICON,
            keywords: [],
            keywordDict: {},
            viewRoles: {},
            renderOptions: JSONUtils.parse(anApplicationRow.cif.RENDER_OPTIONS, {}),
            applicationModules: [],
            reports: []
        }

        _.forEach(anApplicationRow.cif.VIEW_ROLES.split(","), (aRoleId) => {
            if (_.isEmpty(aRoleId)) {
                return
            }
            if (usrContext.hasRoleId(aRoleId))
                anApplication.viewRoles[aRoleId] = true

        })


        _.forEach(anApplicationRow.WB_UIAppReportRows, (aReportRow: WB_UIAppReportRow) => {
            if (!aReportRow.isActive || _.isUndefined(aReportRow.WB_ReportTileRow) || !aReportRow.WB_ReportTileRow.isActive)
                return

            let aReport = self.registerApplicationReportRow(aReportRow)
            if (!_.isEmpty(aReport))
                if (_.filter(anApplication.reports, aRpt => aRpt.wb_id == aReport.wb_id).length)
                    _.first(_.filter(anApplication.reports, aRpt => aRpt.wb_id == aReport.wb_id)).module_id = aReport.module_id
                else
                    anApplication.reports.push(aReport)
        })

        _.forEach(anApplicationRow.WB_UIAppModuleRows, (aModuleRow) => {
            if (!aModuleRow.isActive || _.isUndefined(aModuleRow.WB_UIAppExecutableRow) || !aModuleRow.WB_UIApplicationRow.isActive)
                return

            let anExecutable = self.registerApplicationExecutableRow(aModuleRow.WB_UIAppExecutableRow)
            if (_.isEmpty(anExecutable)) {
                return
            }


            let appModule: IApplicationModule = {
                wb_id: aModuleRow.cif.WB_UI_APPMODULE_ID,
                name: aModuleRow.cif.APPMODULE_NAME,
                shortName: aModuleRow.cif.APPMODULE_SHORTNAME,
                startupPolicy: aModuleRow.cif.STARTUP_POLICY,
                placementPolicy: aModuleRow.cif.PLACEMENT_POLICY,
                launchPolicy: aModuleRow.cif.LAUNCH_POLICY,
                sort: aModuleRow.cif.SORT_ORDER,
                comments: aModuleRow.cif.COMMENTS,
                viewRoles: {},
                editRoles: {},
                executable: anExecutable,
                executableParams: JSONUtils.parse(aModuleRow.cif.EXEC_PARAMETERS, {}),
                ui5Icon: aModuleRow.cif.UI5_ICON,
                renderOptions: JSONUtils.parse(aModuleRow.cif.RENDER_OPTIONS, {})
            }

            if (!_.isEmpty(aModuleRow.cif.KEYWORDS)) {
                _.forEach(aModuleRow.cif.KEYWORDS.split("\n"), (keyword) => {
                    if (_.isEmpty(keyword)) {
                        return
                    }
                    if (keyword in anApplication.keywordDict) {
                        return
                    }
                    anApplication.keywordDict[keyword] = true
                    anApplication.keywords.push(keyword)
                })
            }

            _.forEach(aModuleRow.cif.VIEW_ROLES.split(","), (aRoleId) => {
                if (_.isEmpty(aRoleId)) {
                    return
                }
                if (usrContext.hasRoleId(aRoleId))
                    appModule.viewRoles[aRoleId] = true

            })

            _.forEach(aModuleRow.cif.EDIT_ROLES.split(","), (aRoleId) => {
                if (_.isEmpty(aRoleId)) {
                    return
                }
                if (usrContext.hasRoleId(aRoleId))
                    appModule.editRoles[aRoleId] = true
            })

            if (!_.isEmpty(appModule.viewRoles) || !_.isEmpty(appModule.editRoles))
                anApplication.applicationModules.push(appModule)
            else {
                _.remove(anApplication.reports, function (aReport: IModuleReport) {
                    return aReport.module_id == aModuleRow.cif.WB_UI_APPMODULE_ID.toString()
                });
            }


        }) // Next Module

        if (anApplication.applicationModules.length && !_.isEmpty(anApplication.viewRoles)) {
            self._dctApplications[anApplication.wb_id] = anApplication
            self._applications.push(anApplication)
            return anApplication
        }

        return undefined
    }

    private registerApplicationExecutableRow(anAppExecutableRow: WB_UIAppExecutableRow): IExecutable {
        const self = this

        if (_.isEmpty(anAppExecutableRow)) {
            return undefined
        }
        if (anAppExecutableRow.cif.WB_UI_APPEXEC_ID in self._dctExecutable) {
            return self._dctExecutable[anAppExecutableRow.cif.WB_UI_APPEXEC_ID]
        }

        let anExecutable = new Executable()

        anExecutable.wb_id = anAppExecutableRow.cif.WB_UI_APPEXEC_ID
        anExecutable.id_ui = anAppExecutableRow.cif.APPEXEC_ID
        anExecutable.name = anAppExecutableRow.cif.APPEXEC_NAME
        anExecutable.type = anAppExecutableRow.cif.APPEXEC_TYPE
        anExecutable.help_url = anAppExecutableRow.cif.HELP_URL
        anExecutable.viewRoles = {}
        anExecutable.editRoles = {}

        anExecutable.boClass = PAEnvironment.getRegisteredBusinessObjectClass(anAppExecutableRow.cif.BO_CLASS)
        anExecutable.boClassName = anAppExecutableRow.cif.BO_CLASS
        anExecutable.boCreateOptions = JSONUtils.parse(anAppExecutableRow.cif.BO_CREATEOPTIONS, {})
        anExecutable.boCreateNewOptions = JSONUtils.parse(anAppExecutableRow.cif.BO_CREATENEWOPTIONS, {})
        anExecutable.boCreateNewWizardId = anAppExecutableRow.cif.BO_CREATENEWWIZARD
        anExecutable.boCreateNewWizard = PAEnvironment.getRegisteredBusinessObjectUIWizard(anAppExecutableRow.cif.BO_CLASS, anAppExecutableRow.cif.BO_CREATENEWWIZARD)

        anExecutable.boAfterCreateNewWizardId = anAppExecutableRow.cif.BO_AFTERCREATENEWWIZARD
        anExecutable.boAfterCreateNewWizard = PAEnvironment.getRegisteredBusinessObjectUIWizard(anAppExecutableRow.cif.BO_CLASS, anAppExecutableRow.cif.BO_AFTERCREATENEWWIZARD)
        anExecutable.boCopyOptions = JSONUtils.parse(anAppExecutableRow.cif.BO_COPYOPTIONS, {})
        anExecutable.boCopyWizardId = anAppExecutableRow.cif.BO_COPYWIZARD
        anExecutable.boCopyWizard = PAEnvironment.getRegisteredBusinessObjectUIWizard(anAppExecutableRow.cif.BO_CLASS, anAppExecutableRow.cif.BO_COPYWIZARD)
        anExecutable.boAfterCopyWizardId = anAppExecutableRow.cif.BO_AFTERCOPYWIZARD
        anExecutable.boAfterCopyWizard = PAEnvironment.getRegisteredBusinessObjectUIWizard(anAppExecutableRow.cif.BO_CLASS, anAppExecutableRow.cif.BO_AFTERCOPYWIZARD)

        anExecutable.boLoadOptions = JSONUtils.parse(anAppExecutableRow.cif.BO_LOADOPTIONS, {})
        anExecutable.boCreateNewOnSessionCreate = anAppExecutableRow.cif.BO_CREATENEWONSESSSIONCREATE == "1"
        anExecutable.searchClass = PAEnvironment.getRegisteredBusinessObjectSearch(anAppExecutableRow.cif.BO_CLASS, anAppExecutableRow.cif.SEARCH_CLASS)?.search
        if (!_.isEmpty(anExecutable.searchClass))
            anExecutable.searchClass.toJSON = function () {
                return {}
            }
        anExecutable.searchOptions = JSONUtils.parse(anAppExecutableRow.cif.SEARCH_OPTIONS, {})
        anExecutable.renderOptions = JSONUtils.parse(anAppExecutableRow.cif.RENDER_OPTIONS, {})
        anExecutable.ui5ResourceRoot = anAppExecutableRow.cif.UI5_RESOURCE_ROOT
        anExecutable.ui5ResourceRootPath = anAppExecutableRow.cif.UI5_RESOURCE_ROOT_PATH
        anExecutable.ui5ComponentId = anAppExecutableRow.cif.UI5_COMPONENT_ID
        anExecutable.ui5Icon = anAppExecutableRow.cif.UI5_ICON
        anExecutable.headerBarShowInd = anAppExecutableRow.cif.HEADERBAR_SHOW_IND == "1"
        anExecutable.ui5HeaderBarId = anAppExecutableRow.cif.UI5_HEADERBAR_ID
        anExecutable.footerbarShowInd = anAppExecutableRow.cif.FOOTERBAR_SHOW_IND == "1"
        anExecutable.ui5FooterbarId = anAppExecutableRow.cif.UI5_FOOTERBAR_ID
        anExecutable.tbCreateNewShowInd = anAppExecutableRow.cif.TB_CREATENEW_SHOW_IND == "1"
        anExecutable.tbSearchShowInd = anAppExecutableRow.cif.TB_SEARCH_SHOW_IND == "1"
        anExecutable.tbSaveShowInd = anAppExecutableRow.cif.TB_SAVE_SHOW_IND == "1"
        anExecutable.tbDeleteShowInd = anAppExecutableRow.cif.TB_DELETE_SHOW_IND == "1"
        anExecutable.tbCloseShowInd = anAppExecutableRow.cif.TB_CLOSE_SHOW_IND == "1"
        anExecutable.tbCopyShowInd = anAppExecutableRow.cif.TB_COPY_SHOW_IND == "1"
        anExecutable.tbCreateNoteShowInd = anAppExecutableRow.cif.TB_CREATENOTE_SHOW_IND == "1"
        anExecutable.tbReloadShowInd = anAppExecutableRow.cif.TB_RELOAD_SHOW_IND == "1"
        anExecutable.tbFileListShowInd = anAppExecutableRow.cif.TB_FILELIST_SHOW_IND == "1"
        anExecutable.tbIdDialogShowInd = anAppExecutableRow.cif.TB_ID_DIALOG_SHOW_IND == "1"
        anExecutable.bCacheBO=anAppExecutableRow.cif.NAVIGATION_BO_CACHE_IND=='1'

        let usrContext: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)

        _.forEach(anAppExecutableRow.cif.VIEW_ROLES.split(","), (aRoleId) => {
            if (_.isEmpty(aRoleId)) {
                return
            }
            if (usrContext.hasRoleId(aRoleId))
                anExecutable.viewRoles[aRoleId] = true
            else
                anExecutable.viewRoles[aRoleId] = false
        })

        _.forEach(anAppExecutableRow.cif.EDIT_ROLES.split(","), (aRoleId) => {
            if (_.isEmpty(aRoleId)) {
                return
            }
            if (usrContext.hasRoleId(aRoleId))
                anExecutable.editRoles[aRoleId] = true
            else
                anExecutable.editRoles[aRoleId] = false
        })

        self._dctExecutable[anAppExecutableRow.cif.WB_UI_APPEXEC_ID] = anExecutable
        self._executables.push(anExecutable)

        return anExecutable
    }


    private registerApplicationReportRow(anAppReportRow: WB_UIAppReportRow): IModuleReport {
        const self = this

        if (_.isEmpty(anAppReportRow) || _.isEmpty(anAppReportRow.WB_ReportTileRow)) {
            return undefined
        }
        let rptId: string = anAppReportRow.cif.WB_UI_APP_ID.toString() + '-' + anAppReportRow.cif.WB_REPORTTILE_ID.toString()
        if (rptId in self._dctReport) {
            if (_.isEmpty(self._dctReport[rptId]))
                return undefined
            if (!self._dctReport[rptId].module_id.split("|").includes(anAppReportRow.cif.WB_UI_APPMODULE_ID.toString()))
                self._dctReport[rptId].module_id += "|" + anAppReportRow.cif.WB_UI_APPMODULE_ID.toString()
            return self._dctReport[rptId]
        }

        let aReport = new ModuleReport()

        aReport.wb_id = anAppReportRow.cif.WB_REPORTTILE_ID
        aReport.id_ui = anAppReportRow.WB_ReportTileRow.cif.REPORT_ID
        aReport.name = anAppReportRow.WB_ReportTileRow.cif.REPORT_NAME
        aReport.treePath = anAppReportRow.cif.TREE_PATH
        aReport.module_id = anAppReportRow.cif.WB_UI_APPMODULE_ID.toString()
        aReport.sort = anAppReportRow.cif.SORT_ORDER


        self._dctReport[rptId] = aReport
        self._reports.push(aReport)


        return aReport
    }


    get ApplicationSuiteTypes(): IApplicationSuiteType[] {
        return this._applicationSuiteTypes;
    }

    get UIProfiles(): IUIProfile[] {
        return this._uiProfiles;
    }

    get ApplicationSuites(): IApplicationSuite[] {
        return this._applicationSuites;
    }

    get Applications(): IApplication[] {
        return this._applications
    }

    get Executables(): IExecutable[] {
        return this._executables;
    }

    get Reports(): IModuleReport[] {
        return this._reports
    }

    getApplicationSuite(anApplicationSuiteId: string | number): IApplicationSuite {

        if (anApplicationSuiteId in this._dctApplicationSuites) {
            // @ts-ignore
            return this._dctApplicationSuites[anApplicationSuiteId]
        }

        // Find it using the ID field
        let returnValue: IApplicationSuite = undefined

        _.forEach(this._applicationSuites, (a) => {
            if (a.id_ui == anApplicationSuiteId) {
                returnValue = a
                return
            }
        })

        return returnValue
    }


    getApplication(anApplicationId: string | number): IApplication {

        if (anApplicationId in this._dctApplications) {
            // @ts-ignore
            return this._dctApplications[anApplicationId]
        }

        // Find it using the ID field
        let returnValue: IApplication = undefined

        _.forEach(this._applications, (a) => {
            if (a.id_ui == anApplicationId) {
                returnValue = a
                return
            }
        })

        return returnValue
    }


    getExecutable(anExecutableId: string | number): IExecutable {

        if (anExecutableId in this._dctExecutable) {
            // @ts-ignore
            return this._dctExecutable[anApplicationId]
        }

        // Find it using the ID field
        let returnValue: IExecutable = undefined

        _.forEach(this._executables, (a) => {
            if (a.id_ui == anExecutableId) {
                returnValue = a
                return
            }
        })

        return returnValue
    }

    getAppModule(anAppId: string | number, anExecutableId: string | number): IApplicationModule {
        let returnValue: IApplicationModule = undefined
        _.forEach(_.filter(this._applications, anApp => anApp.wb_id == anAppId), anApp => {
            _.forEach(anApp.applicationModules, aModule => {
                if (aModule.executable.id_ui == anExecutableId) {
                    returnValue = aModule
                    return false
                }
            })
            if(returnValue)
                return false
        })

        return returnValue
    }

}

class Executable implements IExecutable {

    dummyFunction() {
    } // establish a prototype, jQuery.extend skips
    wb_id: number = undefined
    id_ui: string = undefined
    name: string = undefined
    help_url: string = undefined
    boClass: { new(options?: any): IBusinessObject } = undefined
    boClassName:string=undefined
    boCopyOptions: { [p: string]: any } = undefined
    boCopyWizard: () => any = undefined
    boCopyWizardId: string = undefined
    boAfterCopyWizard: () => any = undefined
    boAfterCopyWizardId: string = undefined
    boCreateNewOnSessionCreate: boolean = undefined
    boCreateNewOptions: { [p: string]: any } = undefined
    boCreateNewWizard: () => any = undefined
    boAfterCreateNewWizard: () => any = undefined
    boCreateNewWizardId: string = undefined
    boAfterCreateNewWizardId: string = undefined
    boCreateOptions: { [p: string]: any } = undefined
    boLoadOptions: { [p: string]: any } = undefined
    editRoles: { [p: string]: boolean } = undefined
    footerbarShowInd: boolean = undefined
    headerBarShowInd: boolean = undefined
    renderOptions: { [p: string]: any } = undefined
    searchClass: ISearch = undefined
    searchOptions: { [p: string]: any } = undefined
    tbCloseShowInd: boolean = undefined
    tbCopyShowInd: boolean = undefined
    tbCreateNewShowInd: boolean = undefined
    tbCreateNoteShowInd: boolean = undefined
    tbDeleteShowInd: boolean = undefined
    tbFileListShowInd: boolean = undefined
    tbIdDialogShowInd: boolean = undefined
    tbReloadShowInd: boolean = undefined
    tbSaveShowInd: boolean = undefined
    tbSearchShowInd: boolean = undefined
    type: string = undefined
    ui5ComponentId: string = undefined
    ui5FooterbarId: string = undefined
    ui5HeaderBarId: string = undefined
    ui5Icon: string = undefined
    ui5ResourceRoot: string = undefined
    ui5ResourceRootPath: string = undefined
    viewRoles: { [p: string]: boolean } = undefined
    reportId: number = undefined
    bCacheBO:boolean =  undefined
}

class ModuleReport implements IModuleReport {

    dummyFunction() {
    } // establish a prototype, jQuery.extend skips
    wb_id: number = undefined
    id_ui: string = undefined
    name: string = undefined
    treePath: string = undefined
    module_id: string = undefined
    sort: number = 0
}
